package com.snakesworld.game.domain

import com.snakesworld.game.network.NicknameValidator

/**
 * How a nickname is shown to one player. Blocked ids keep their snake in the
 * match, but the name is replaced with a generic label for the blocker only.
 */
object NicknameDisplay {
    const val ELLIPSIS: String = "…"

    fun visibleName(
        raw: String,
        playerId: String,
        blockedIds: Set<String>,
        hiddenLabel: String,
    ): String {
        if (playerId.isNotEmpty() && playerId in blockedIds) return hiddenLabel
        return NicknameValidator.forDisplay(raw)
    }

    /**
     * Single-line truncation. [measure] is the rendered width of a candidate.
     * The full string is returned when it fits; otherwise a prefix plus [ELLIPSIS].
     */
    fun ellipsizeToWidth(
        text: String,
        maxWidth: Float,
        measure: (String) -> Float,
    ): String {
        if (text.isEmpty() || maxWidth <= 0f) return ""
        if (measure(text) <= maxWidth) return text
        val ellipsisWidth = measure(ELLIPSIS)
        if (ellipsisWidth > maxWidth) return ""
        var low = 0
        var high = text.length
        var best = ELLIPSIS
        while (low <= high) {
            val mid = (low + high) ushr 1
            val candidate = text.take(mid).trimEnd() + ELLIPSIS
            if (measure(candidate) <= maxWidth) {
                best = candidate
                low = mid + 1
            } else {
                high = mid - 1
            }
        }
        return best
    }
}

/** One other player who can be reported or blocked from inside a match. */
data class ArenaPlayerRef(
    val id: String,
    val rawName: String,
)

/**
 * Other players in the current match. Remote snakes win over the leaderboard
 * snapshot when both list the same id. The local player is omitted.
 */
fun arenaOpponents(
    localPlayerId: String,
    remotePlayers: List<ArenaPlayerRef>,
    leaderboard: List<ArenaPlayerRef>,
): List<ArenaPlayerRef> {
    val byId = LinkedHashMap<String, ArenaPlayerRef>()
    for (player in remotePlayers) {
        if (player.id.isBlank() || player.id == localPlayerId) continue
        byId[player.id] = player
    }
    for (player in leaderboard) {
        if (player.id.isBlank() || player.id == localPlayerId) continue
        if (player.id !in byId) byId[player.id] = player
    }
    return byId.values.sortedWith(compareBy({ it.rawName.lowercase() }, { it.id }))
}
